import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { BotAvatarSet } from "@rakazo/contracts";
import { isBotAvatarValue, PROFILE_IMAGE_MAX_BYTES, resolveBotAvatarSet } from "@rakazo/contracts";
import { SHIPPED_BOT_AVATAR_SHAPE_KEYS } from "@rakazo/core";
import {
  BOT_AVATAR_COLORS,
  BotAvatar,
  BotShapePreview,
  DEFAULT_BOT_AVATAR_COLOR,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  defaultBotAvatarValue,
  parseBotAvatar,
  PhotoCropper,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@rakazo/ui-web";
import { Button } from "@rakazo/ui-web/components/ui/button";
import { Check, Pencil, Upload, X } from "lucide-react";
import type { ClipboardEvent, DragEvent } from "react";
import { useEffect, useRef, useState } from "react";

/**
 * The shape grid and color row for a bot's look. With `organicIdentities`, the grid also offers
 * the organic body each identity draws (for a bot that has no id yet); without them, "Default"
 * restores the bot's own organic body.
 */
export function BotLookPicker({
  value,
  identity,
  organicIdentities,
  avatarSet = "organic",
  onAvatarSetChange,
  onChange,
}: {
  value: string;
  identity?: string;
  organicIdentities?: readonly string[];
  avatarSet?: BotAvatarSet;
  onAvatarSetChange?: (set: BotAvatarSet, value: string) => void;
  onChange: (value: string, identity?: string) => void;
}) {
  const parsed = parseBotAvatar(value);
  const currentColor = parsed.color || DEFAULT_BOT_AVATAR_COLOR;
  const isDefaultLook = !parsed.isImage && parsed.shapeIndex === undefined;

  function selectColor(color: string) {
    onChange(parsed.shapeIndex === undefined ? color : `${color}::shape_${parsed.shapeIndex}`);
  }

  return (
    <div className="space-y-4">
      <Tabs
        value={avatarSet}
        onValueChange={(next) =>
          onAvatarSetChange?.(resolveBotAvatarSet(next), defaultBotAvatarValue(value))
        }
      >
        <TabsList className="w-full" aria-label={t`Icon set`}>
          <TabsTrigger value="organic" className="flex-1">
            <Trans>Original</Trans>
          </TabsTrigger>
          <TabsTrigger value="rakazo" className="flex-1">
            <Trans>Wurkees</Trans>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {avatarSet === "organic" ? (
        <div>
          {organicIdentities ? null : (
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
                <Trans>Shape</Trans>
              </div>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                data-testid="avatar-studio-default"
                aria-pressed={isDefaultLook}
                onClick={() => {
                  const next = defaultBotAvatarValue(value);
                  if (next !== value) onChange(next);
                }}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  isDefaultLook
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Trans>Default</Trans>
              </Button>
            </div>
          )}
          <div
            className={`grid place-items-center gap-2 ${organicIdentities ? "grid-cols-4 sm:grid-cols-8 sm:gap-1" : "grid-cols-4"}`}
          >
            {organicIdentities?.map((organicIdentity, index) => (
              <BotShapePreview
                key={organicIdentity}
                identity={organicIdentity}
                label={t`Shape ${index + 1}`}
                color={currentColor}
                selected={isDefaultLook && identity === organicIdentity}
                onClick={() => onChange(currentColor, organicIdentity)}
              />
            ))}
            {SHIPPED_BOT_AVATAR_SHAPE_KEYS.map((key, shapeIndex) => (
              <BotShapePreview
                key={key}
                shapeIndex={shapeIndex}
                color={currentColor}
                selected={!parsed.isImage && parsed.shapeIndex === shapeIndex}
                onClick={() => onChange(`${currentColor}::shape_${shapeIndex}`)}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="border-t border-border pt-2">
        {organicIdentities ? null : (
          <div className="mb-2 text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
            <Trans>Color</Trans>
          </div>
        )}
        <div className="grid grid-cols-6 place-items-center gap-2">
          {BOT_AVATAR_COLORS.map((color) => {
            const selected = currentColor.toLowerCase() === color.toLowerCase() && !parsed.isImage;
            return (
              <button
                key={color}
                type="button"
                onClick={() => selectColor(color)}
                aria-label={t`Color ${color}`}
                aria-pressed={selected}
                className={`size-6 rounded-full border transition-transform hover:scale-110 active:scale-95 focus-visible:ring-2 focus-visible:ring-ring ${
                  selected
                    ? "scale-105 border-transparent ring-2 ring-foreground ring-offset-2 ring-offset-popover"
                    : "border-border"
                }`}
                style={{ backgroundColor: color }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export interface AvatarStudioPopoverProps {
  value: string;
  identity?: string;
  status?: string;
  avatarSet?: BotAvatarSet;
  size?: number;
  onChange: (value: string, avatarSet?: BotAvatarSet) => void;
  disabled?: boolean;
}

export function AvatarStudioPopover({
  value,
  identity,
  status,
  avatarSet = "organic",
  size = 72,
  onChange,
  disabled = false,
}: AvatarStudioPopoverProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"bot" | "upload">("bot");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRequest = useRef(0);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [readingImage, setReadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  useEffect(
    () => () => {
      imageRequest.current++;
    },
    [],
  );

  function changeOpen(next: boolean) {
    if (!next) {
      imageRequest.current++;
      setCropSource(null);
      setReadingImage(false);
      setImageError(null);
    }
    setOpen(next);
  }

  function restoreDefaultAvatar() {
    const next = defaultBotAvatarValue(value);
    if (next !== value) onChange(next);
  }

  async function processImageFile(file: File) {
    if (
      !["image/png", "image/jpeg", "image/webp", "image/gif"].includes(file.type) ||
      file.size > PROFILE_IMAGE_MAX_BYTES
    ) {
      setImageError(t`Choose a PNG, JPEG, WebP or GIF image up to 5 MB.`);
      return;
    }
    const current = ++imageRequest.current;
    setReadingImage(true);
    setImageError(null);
    try {
      const src = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          typeof reader.result === "string"
            ? resolve(reader.result)
            : reject(new Error("Image read failed"));
        reader.onerror = () => reject(new Error("Image read failed"));
        reader.readAsDataURL(file);
      });
      if (current === imageRequest.current) setCropSource(src);
    } catch {
      if (current === imageRequest.current)
        setImageError(t`Could not read this image. Choose another file.`);
    } finally {
      if (current === imageRequest.current) setReadingImage(false);
    }
  }

  function handleDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) processImageFile(file);
  }

  function handlePaste(event: ClipboardEvent<HTMLButtonElement>) {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      if (!item?.type.startsWith("image/")) continue;
      const file = item.getAsFile();
      if (file) {
        processImageFile(file);
        break;
      }
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => changeOpen(true)}
        className="group relative cursor-pointer rounded-2xl outline-none transition-transform hover:scale-[1.03] focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={t`Customize bot avatar`}
        data-testid="avatar-studio-trigger"
      >
        <BotAvatar
          color={value}
          identity={identity}
          size={size}
          status={status}
          avatarSet={avatarSet}
        />
        <div className="absolute -right-1 -bottom-1 flex size-6 items-center justify-center rounded-full border-2 border-background bg-secondary text-foreground shadow-md transition-transform group-hover:scale-110">
          <Pencil size={12} strokeWidth={2.2} />
        </div>
      </button>

      <Dialog open={open} onOpenChange={changeOpen}>
        <DialogContent
          showCloseButton={false}
          className={`max-h-[calc(100dvh-24px)] max-w-[calc(100vw-24px)] gap-4 overflow-y-auto rounded-3xl p-5 ${cropSource ? "w-[520px] sm:max-w-[520px]" : "w-[360px] sm:max-w-[360px]"}`}
          data-testid="avatar-studio"
        >
          <DialogHeader
            className={
              cropSource
                ? "relative items-center space-y-2 text-center"
                : "flex-row items-center justify-between space-y-0"
            }
          >
            <DialogTitle className="text-[14px] font-semibold tracking-tight">
              {cropSource ? <Trans>Adjust Photo</Trans> : <Trans>Avatar Studio</Trans>}
            </DialogTitle>
            <DialogDescription
              className={cropSource ? "text-base text-muted-foreground" : "sr-only"}
            >
              {cropSource ? (
                <Trans>Drag to move, use the slider to zoom.</Trans>
              ) : (
                <Trans>Choose a bot shape, color, or upload an image</Trans>
              )}
            </DialogDescription>
            <Button
              variant="ghost"
              size="icon-sm"
              type="button"
              onClick={() => changeOpen(false)}
              className={`rounded-full text-muted-foreground ${cropSource ? "absolute end-0 top-0" : ""}`}
              aria-label={t`Close`}
            >
              <X size={16} />
            </Button>
          </DialogHeader>

          {imageError ? (
            <p role="alert" className="text-base text-destructive">
              {imageError}
            </p>
          ) : null}
          {cropSource ? (
            <PhotoCropper
              src={cropSource}
              labels={{
                preview: t`Photo crop. Drag or use arrow keys to reposition.`,
                zoom: t`Zoom`,
                zoomIn: t`Zoom in`,
                zoomOut: t`Zoom out`,
                cancel: t`Cancel`,
                reset: t`Reset`,
                apply: t`Apply Crop`,
                error: t`Could not crop this image. Choose another photo.`,
                loading: t`Loading photo…`,
              }}
              onCancel={() => {
                setCropSource(null);
                setImageError(null);
              }}
              onApply={(cropped) => {
                if (!isBotAvatarValue(cropped)) {
                  setImageError(t`Could not save this photo. Try a smaller image.`);
                  return;
                }
                onChange(cropped);
                changeOpen(false);
              }}
            />
          ) : (
            <>
              <div className="flex flex-col items-center justify-center gap-3 py-2">
                <BotAvatar
                  color={value}
                  identity={identity}
                  size={78}
                  status={status}
                  avatarSet={avatarSet}
                />
                {parseBotAvatar(value).isImage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setImageError(null);
                      setCropSource(value);
                    }}
                  >
                    <Trans>Adjust Photo</Trans>
                  </Button>
                ) : null}
              </div>

              <Tabs
                value={activeTab}
                onValueChange={(next) => setActiveTab(next as "bot" | "upload")}
                className="gap-4"
              >
                <div className="flex items-center justify-between border-b border-border pb-1">
                  <TabsList className="rounded-full">
                    <TabsTrigger value="bot" className="rounded-full px-3 text-xs">
                      <Trans>Bot</Trans>
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="rounded-full px-3 text-xs">
                      <Trans>Upload</Trans>
                    </TabsTrigger>
                  </TabsList>

                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={restoreDefaultAvatar}
                    className="text-xs text-muted-foreground"
                  >
                    <Trans>Reset</Trans>
                  </Button>
                </div>

                <TabsContent value="bot" className="pt-1" data-testid="avatar-studio-bot-tab">
                  <BotLookPicker
                    value={value}
                    identity={identity}
                    avatarSet={avatarSet}
                    onChange={(next) => onChange(next)}
                    onAvatarSetChange={(set, next) => onChange(next, set)}
                  />
                </TabsContent>
                <TabsContent value="upload">
                  <button
                    type="button"
                    aria-label={t`Image upload area`}
                    disabled={readingImage}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onPaste={handlePaste}
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex w-full flex-col items-center justify-center rounded-xl border border-dashed p-6 text-center transition-colors ${
                      dragOver
                        ? "border-primary bg-primary/10"
                        : "border-border bg-muted hover:border-foreground/30"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void processImageFile(file);
                        event.target.value = "";
                      }}
                    />
                    <div className="mb-2 grid size-10 place-items-center rounded-full bg-secondary text-muted-foreground">
                      <Upload size={18} strokeWidth={1.8} />
                    </div>
                    <p className="text-[12.5px] font-medium text-muted-foreground">
                      <Trans>Drag, drop, or paste an image</Trans>
                    </p>
                    <span className="mt-3 rounded-lg bg-secondary px-3 py-1.5 text-xs font-medium text-foreground">
                      {readingImage ? <Trans>Reading image…</Trans> : <Trans>Choose file</Trans>}
                    </span>
                  </button>
                </TabsContent>
              </Tabs>

              <DialogFooter className="-mx-5 -mb-5 border-border px-5 sm:justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => changeOpen(false)}
                >
                  <Check size={14} />
                  <Trans>Done</Trans>
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
